<script>
  import { onMount } from 'svelte';
  import { Button, DataTable, InlineNotification, Modal, Select, SelectItem, Toolbar, ToolbarContent } from 'carbon-components-svelte';
  import FilterIcon from 'carbon-icons-svelte/lib/Filter16';
  import { JsonView } from '@zerodevx/svelte-json-view';
  import { useLocation } from 'svelte-navigator';
  import isEqual from 'lodash/isEqual';

  import LoadingSpinner from '../../components/LoadingSpinner.svelte';
  import PaginationWithRouting from '../../components/PaginationWithRouting.svelte';
  import RelativeDate from '../../components/RelativeDate.svelte';
  import TaskExecutionDuration from './TaskExecutionDuration.svelte';
  import TaskExecutionDetails from './TaskExecutionDetails.svelte';

  import { cancelExecution, getExecutions, getTasks } from '../../services';
  import { messagesStore } from '../../stores';

  const location = useLocation();

  let executionDetailsModal;
  let pageData = { loading: true };
  let query = { currentPage: 1, pageSize: 25 };
  let previousQuery = { ...query };
  let tasks = [];
  let selectedTaskId = '';

  onMount(async () => {
    try {
      tasks = await getTasks();
    } catch (error) {
      console.error('[TaskExecutions] Failed to load tasks for filter!', error);
    }
  });

  function handleTaskFilterChange({ detail: taskId }) {
    selectedTaskId = taskId;
    query.currentPage = 1;
  }

  $: runningTaskExecutionsCount = $messagesStore.runningTaskExecutions?.length ?? 0;
  $: relatedEntityId = new URLSearchParams($location.search)?.get('relatedEntityId');
  // We are loading loadExecutions function on `runningTaskExecutionsCount` change or when task filter changes
  $: loadExecutions(runningTaskExecutionsCount, selectedTaskId);

  async function loadExecutions() {
    pageData = { ...pageData, loading: true };
    try {
      const { currentPage, pageSize: limit } = query;
      const { count: executionCount, results: executions } = await getExecutions({
        limit,
        offset: (currentPage - 1) * limit,
        relatedEntityId,
        taskId: selectedTaskId || undefined,
      });
      // eslint-disable-next-line svelte/infinite-reactive-loop
      pageData = { executionCount, executions, loading: false };
    } catch (error) {
      console.error('[TaskExecutions] Failed to load executions!', error);
      // eslint-disable-next-line svelte/infinite-reactive-loop
      pageData = { loading: false, error };
    }
  }

  function cancelTaskExecution(executionId) {
    return async function cancel() {
      pageData = { ...pageData, loading: true, cancelationError: null };

      try {
        await cancelExecution(executionId);
        pageData = { ...pageData, loading: false };
      } catch (error) {
        console.error('[TaskList] Failed to cancel execution!', error);
        pageData = { ...pageData, loading: false, cancelationError: error };
      }
    };
  }

  $: (async () => {
    if (pageData.loading || isEqual(previousQuery, query)) {
      return;
    }

    previousQuery = { ...query };
    // eslint-disable-next-line svelte/infinite-reactive-loop
    loadExecutions();
  })();
</script>

{#if pageData.error}
  <InlineNotification
    hideCloseButton
    kind="error"
    lowContrast
    title="Error:"
    subtitle="Failed to load executions! Please try again later."
  />
{:else}
  {#if pageData.cancelationError}
    <InlineNotification kind="error" lowContrast title="Error:" subtitle="Failed to cancel execution! Please try again later." />
  {/if}
  <LoadingSpinner loading={pageData.loading}>
    <DataTable
      headers={[
        { key: 'task.name', value: 'Task Name' },
        { key: 'statusDescription', value: 'Status' },
        { key: 'createdByDisplayName', value: 'Triggered By' },
        { key: 'createdAt', value: 'Triggered' },
        { key: 'executionTime', value: 'Execution Time' },
        { key: 'actions', empty: true },
      ]}
      rows={pageData?.executions ?? []}
    >
      <Toolbar>
        <ToolbarContent>
          <div class="table-filters">
            <FilterIcon />
            <Select
              inline
              class="filter-selector"
              size="sm"
              labelText="Task"
              hideLabel
              selected={selectedTaskId}
              on:update={handleTaskFilterChange}
            >
              <SelectItem value="" text="All Tasks" />
              {#each tasks as task (task.id)}
                <SelectItem value={task.id} text={task.name} />
              {/each}
            </Select>
          </div>
          <div class="toolbar-spacer" />
        </ToolbarContent>
      </Toolbar>
      <span slot="cell" let:cell let:row>
        {#if cell.key === 'actions'}
          {#if row.data}
            <TaskExecutionDetails
              {row}
              on:showExecutionDetailsModal={({ detail: data }) => {
                executionDetailsModal = data;
              }}
            />
          {:else if row.status === 'running'}
            <Button size="small" kind="ghost" on:click={cancelTaskExecution(row.id)}>Cancel</Button>
          {/if}
        {:else if cell.key === 'executionTime'}
          <TaskExecutionDuration {row} />
        {:else if cell.key === 'statusDescription'}
          <div class="running-cell-wrapper">
            {#if cell.value === 'Running'}
              <div>Running...</div>
              <LoadingSpinner centered={false} compact inlineLeftMargin withOverlay={false} />
            {:else}
              {cell.value}
            {/if}
          </div>
        {:else if cell.key === 'createdAt'}
          <RelativeDate date={cell.value} />
        {:else if cell.display}
          {cell.display(cell.value, row)}
        {:else}
          {cell.value}
        {/if}
      </span>
    </DataTable>
    <PaginationWithRouting
      bind:page={query.currentPage}
      bind:pageSize={query.pageSize}
      pageSizes={[10, 25, 50]}
      totalItems={pageData.executionCount ?? 0}
    />
  </LoadingSpinner>
{/if}

<Modal
  modalHeading="Execution Details"
  preventCloseOnClickOutside={true}
  size="lg"
  passiveModal
  on:click:button--secondary={() => {
    executionDetailsModal = false;
  }}
  on:close={() => {
    executionDetailsModal = false;
  }}
  bind:open={executionDetailsModal}
>
  <JsonView json={executionDetailsModal} />
</Modal>

<style>
  .running-cell-wrapper {
    display: inline-flex;
    font-weight: 600;
  }

  .table-filters {
    align-items: center;
    display: flex;
    padding: 0 1rem;
  }

  .table-filters :global(.bx--select--inline .bx--select-input) {
    max-width: 150px;
  }

  .toolbar-spacer {
    flex: 1 1 auto;
  }
</style>
